import { Component, Input, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { CoalService } from 'app/services/coal.service';
import Swal from 'sweetalert2';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, switchMap, take, withLatestFrom, zip } from 'rxjs';
import { BaseService } from 'app/services/base.service';
import { AppState, selectAuthState, selectUtilsState } from 'app/store/app.states';
import { Store } from '@ngrx/store';
import { SeleccionarEmpresa } from 'app/store/actions/utils.action';
import { UpdateData } from 'app/store/actions/auth.actions';
import { Router } from '@angular/router';
@Component({
    selector: 'app-wizzard-company',
    templateUrl: './wizzard-company.component.html',
    styleUrls: ['./wizzard-company.component.scss']
})
export class WizzardCompanyComponent {
    @ViewChild('stepper') private stepper: MatStepper;

    firstFormGroup: FormGroup;
    secondFormGroup: FormGroup;

    // Formularios y variables de datos de facturacion
    accountForm: FormGroup;
    rfcAvailable: boolean;
    razonSocialAvailable: boolean;
    uuidPattern = /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/;

    // Formularios y variables para sellos
    stampForm: FormGroup;

    // Formulario y variables para lugar de expedicion
    lugarExpedicionForm: FormGroup;

    // Formulario y variables para usuarios
    opcionSeleccionada: string = 'propia';
    nuevoUsuarioForm: FormGroup;
    existingUserId: number | null = null; // Variable para almacenar el ID del usuario seleccionado

    emailAvailable: boolean | null = null;
    fileCer: any;
    fileKey: any;
    idEmpresa: number;

    isSaving: boolean = false;
    progress: number = 0;
    currentProcessText: string = '';

    user;
    state;

    @Input() Observable$: BehaviorSubject<any>;
    isValidCP: boolean = false;

    constructor(
        private _formBuilder: FormBuilder,
        private coalService: CoalService,
        private baseService: BaseService,
        private store: Store<AppState>,
        private router: Router // Inyectar el Router aquí
    ) { }

    ngOnInit() {
        this.firstFormGroup = this._formBuilder.group({
            firstCtrl: ['', Validators.required],
        });
        this.secondFormGroup = this._formBuilder.group({
            secondCtrl: ['', Validators.required],
        });

        this.state = cloneDeep(this.baseService.getUserData());
        this.user = this.state.user;

        // Iniciacion para datos de facturacion
        this.accountForm = this._formBuilder.group({
            rfc: [null, [Validators.required, Validators.pattern(this.uuidPattern)]],
            razonSocial: [null, [Validators.required, Validators.maxLength(350)]],
            nombreComercial: [null, [Validators.maxLength(350)]],
            idTipoPersona: [null, Validators.required],
            idGiro: [null, Validators.required],
            idRegimenFiscal: [null, Validators.required],
            idTamanioEmpresa: [null, Validators.required]
        });

        this.rfcAvailable = false;
        this.razonSocialAvailable = false;

        // Iniciacion para sellos
        this.stampForm = this._formBuilder.group({
            contrasenia: [null, Validators.required],
            cer: [null, Validators.required],
            key: [null, Validators.required]
        });

        // Iniciacion para lugar de expedicion
        this.lugarExpedicionForm = this._formBuilder.group({
            nombreLugar: [null, Validators.required],
            descripcionLugar: [null],
            CP: ['', [Validators.required, Validators.pattern('^[0-9]{5}$'), this.validateCP.bind(this)]],
            estado: [null, Validators.required],
            // Opcionales
            noExterior: [null],
            noInterior: [null],
            colonia: [null],
            municipio: [null],
            pais: [null],
            coloniaManual: [null]
        });

        // iniciacion para usuarios
        this.nuevoUsuarioForm = this._formBuilder.group({
            nombre: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            contrasena: ['', [Validators.required, this.customPasswordValidator()]]
        });
    }
    
    validateCP(control: FormControl): { [key: string]: boolean } | null {
        if (control.value && control.errors && control.errors['notFound']) {
            return { notFound: true };
        }
        return null;
    }

    customPasswordValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) {
                return null;
            }

            const hasUpperCase = /[A-Z]+/.test(value);
            const hasLowerCase = /[a-z]+/.test(value);
            const hasNumeric = /[0-9]+/.test(value);
            const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]+/.test(value);
            const isValidLength = value.length >= 8;
            const hasNoRepeatedSequence = !/(.)\1{2,}/.test(value); // No secuencias de tres o más caracteres iguales

            const passwordValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar && isValidLength && hasNoRepeatedSequence;

            return !passwordValid ? { invalidPassword: true } : null;
        };
    }

    onRfcAvailableChange(available: boolean) {
        this.rfcAvailable = available;
    }

    onRazonSocialAvailableChange(available: boolean) {
        this.razonSocialAvailable = available;
    }

    goToNextStep() {
        if (this.validateStep()) {
            this.stepper.next();
        }
    }

    validateStep(): boolean {
        // Validación del formulario
        if (!this.accountForm.valid) {
            Swal.fire({
                title: 'Formulario incompleto',
                text: 'Debes llenar el formulario con los datos y formatos correctos antes de continuar.',
                icon: 'warning',
                confirmButtonText: 'Aceptar'
            });
            return false;
        }

        // Validación de la disponibilidad del RFC
        if (!this.rfcAvailable) {
            Swal.fire({
                title: 'RFC no disponible',
                text: 'El RFC que ingresaste ya está registrado. Por favor, verifica los datos e intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return false;
        }

        // Validación de la disponibilidad de la razón social
        if (!this.razonSocialAvailable) {
            Swal.fire({
                title: 'Razón social no disponible',
                text: 'La razón social que ingresaste ya está registrada. Por favor, verifica los datos e intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return false;
        }

        // Si todas las validaciones pasan
        return true;
    }

    goToNextStamp() {
        if (this.validateStamp()) {
            this.stepper.next();
        }
    }

    validateStamp(): boolean {
        // Validación del archivo .CER
        if (this.stampForm.controls.cer.errors) {
            if (this.stampForm.controls.cer.errors.required) {
                Swal.fire({
                    title: 'Archivo .CER necesario',
                    text: 'Para continuar, sube el archivo .CER.',
                    icon: 'warning',
                    confirmButtonText: 'De acuerdo'
                });
                return false;
            }
        }

        // Validación del archivo .KEY
        if (this.stampForm.controls.key.errors) {
            if (this.stampForm.controls.key.errors.required) {
                Swal.fire({
                    title: 'Archivo .KEY requerido',
                    text: 'Por favor, sube el archivo .KEY para seguir con el proceso.',
                    icon: 'warning',
                    confirmButtonText: 'De acuerdo'
                });
                return false;
            }
        }

        // Validación del campo Contraseña
        if (this.stampForm.controls.contrasenia.errors) {
            if (this.stampForm.controls.contrasenia.errors.required) {
                Swal.fire({
                    title: 'Falta la contraseña',
                    text: 'Por favor, ingresa la contraseña de los sellos digitales para continuar.',
                    icon: 'warning',
                    confirmButtonText: 'De acuerdo'
                });
                return false;
            }
        }

        // Si todas las validaciones pasan
        return true;
    }

    onFileCerChange(fileCer: any) {
        console.log('Archivo CER recibido en el componente padre:', fileCer);
        // Aquí puedes manejar el valor como lo necesites

        this.fileCer = fileCer
    }

    onFileKeyChange(fileKey: any) {
        console.log('Archivo KEY recibido en el componente padre:', fileKey);
        // Aquí puedes manejar el valor como lo necesites

        this.fileKey = fileKey
    }

    goToNextExpedicion() {
        if (this.validateLugarExpedicion()) {
            this.stepper.next();
        }
    }

    validateLugarExpedicion(): boolean {
        // Validación del campo Nombre del Lugar
        if (this.lugarExpedicionForm.controls.nombreLugar.errors) {
            if (this.lugarExpedicionForm.controls.nombreLugar.errors.required) {
                Swal.fire({
                    title: 'Falta el nombre del lugar',
                    text: 'Por favor, ingresa el nombre del lugar de expedición.',
                    icon: 'warning',
                    confirmButtonText: 'De acuerdo'
                });
                return false;
            }
        }
    
        // Validación del campo Código Postal (CP)
        if (this.lugarExpedicionForm.controls.CP.errors || !this.isValidCP) {
            if (this.lugarExpedicionForm.controls.CP.errors?.required) {
                Swal.fire({
                    title: 'Código Postal necesario',
                    text: 'Por favor, ingresa el código postal.',
                    icon: 'warning',
                    confirmButtonText: 'De acuerdo'
                });
                return false;
            }
            if (this.lugarExpedicionForm.controls.CP.errors?.minlength || this.lugarExpedicionForm.controls.CP.errors?.maxlength) {
                Swal.fire({
                    title: 'Código Postal inválido',
                    text: 'El código postal debe tener exactamente 5 dígitos.',
                    icon: 'warning',
                    confirmButtonText: 'De acuerdo'
                });
                return false;
            }
            if (!this.isValidCP) {
                Swal.fire({
                    title: 'Código Postal no encontrado',
                    text: 'El código postal ingresado no es válido. Por favor, verifica y vuelve a intentarlo.',
                    icon: 'warning',
                    confirmButtonText: 'De acuerdo'
                });
                return false;
            }
        }
    
        // Validación del campo Estado
        if (this.lugarExpedicionForm.controls.estado.errors) {
            if (this.lugarExpedicionForm.controls.estado.errors.required) {
                Swal.fire({
                    title: 'Estado requerido',
                    text: 'Por favor, selecciona el estado correspondiente.',
                    icon: 'warning',
                    confirmButtonText: 'De acuerdo'
                });
                return false;
            }
        }
    
        // Si todas las validaciones pasan
        return true;
    }

    handleCpValidation(isValid: boolean) {
        if (isValid) {
            // Código postal válido
            this.isValidCP = true;
            console.log('Código postal válido');
        } else {
            this.isValidCP = false;
            // Código postal no válido
            console.log('Código postal no válido');
        }
    }

    // Validaciones para finalizar proceso
    goToNextDone() {
        if (this.validateUsuarioSeleccionado()) {
            this.submitAllFormsWithRollback(); // Desencadenar la inserción secuencial
            this.stepper.next();
        }
    }

    onOpcionSeleccionadaChange(nuevaOpcion: string) {
        this.opcionSeleccionada = nuevaOpcion;
        console.log('Opción seleccionada en el padre:', this.opcionSeleccionada);
    }

    validateUsuarioSeleccionado(): boolean {
        // Si la opción seleccionada es 'propia', no se requiere ninguna validación
        if (this.opcionSeleccionada === 'propia') {
            return true;
        }
    
        // Si la opción seleccionada es 'nuevo', se valida el formulario de nuevo usuario
        if (this.opcionSeleccionada === 'nuevo') {
            // Validación del campo Nombre
            if (this.nuevoUsuarioForm.controls.nombre.errors) {
                if (this.nuevoUsuarioForm.controls.nombre.errors.required) {
                    Swal.fire({
                        title: 'Nombre requerido',
                        text: 'Por favor, ingresa el nombre completo del nuevo usuario.',
                        icon: 'warning',
                        confirmButtonText: 'De acuerdo'
                    });
                    return false;
                }
            }
    
            // Validación del campo Email
            if (this.nuevoUsuarioForm.controls.email.errors) {
                if (this.nuevoUsuarioForm.controls.email.errors.required) {
                    Swal.fire({
                        title: 'Correo electrónico requerido',
                        text: 'Por favor, ingresa un correo electrónico válido.',
                        icon: 'warning',
                        confirmButtonText: 'De acuerdo'
                    });
                    return false;
                }
                if (this.nuevoUsuarioForm.controls.email.errors.email) {
                    Swal.fire({
                        title: 'Correo electrónico inválido',
                        text: 'Por favor, ingresa un formato de correo electrónico válido.',
                        icon: 'warning',
                        confirmButtonText: 'De acuerdo'
                    });
                    return false;
                }
            }
    
            // Verificar disponibilidad del correo electrónico
            if (this.emailAvailable === false) {
                Swal.fire({
                    title: 'Correo no disponible',
                    text: 'El correo electrónico del nuevo usuario ya está registrado, por favor, usa otro correo.',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                return false;
            }
    
            // Validación del campo Contraseña
            if (this.nuevoUsuarioForm.controls.contrasena.errors) {
                if (this.nuevoUsuarioForm.controls.contrasena.errors.required) {
                    Swal.fire({
                        title: 'Contraseña requerida',
                        text: 'Por favor, ingresa una contraseña.',
                        icon: 'warning',
                        confirmButtonText: 'De acuerdo'
                    });
                    return false;
                }
            }
        }
    
        // Si la opción seleccionada es 'existente', se valida que se haya seleccionado un usuario
        if (this.opcionSeleccionada === 'existente' && !this.existingUserId) {
            Swal.fire({
                title: 'Usuario requerido',
                text: 'Por favor, selecciona un usuario registrado para gestionar la empresa.',
                icon: 'warning',
                confirmButtonText: 'De acuerdo'
            });
            return false;
        }
    
        // Si todas las validaciones pasan y el correo está disponible
        return true;
    }    

    submitNewUserForm(): Promise<any> {
        return new Promise((resolve, reject) => {
            const params = {
                data: {
                    email: this.nuevoUsuarioForm.value.email,
                    password: this.nuevoUsuarioForm.value.contrasena,
                    name: this.nuevoUsuarioForm.value.nombre,
                    cellphone: '1212121212',
                    ope_empresas: [this.idEmpresa],
                    role: 4
                }
            };
    
            this.coalService.postService('ope-empresas/member', params)
                .subscribe({
                    next: (res: any) => {
                        resolve(res.data); // Resolver la promesa con los datos del usuario
                    },
                    error: (error: any) => {
                        // Manejo de errores personalizados
                        let errorMessage = 'Ocurrió un error al crear el usuario.';
    
                        // Validación específica para el error de correo electrónico duplicado
                        if (error?.error?.message?.includes('email') && error?.error?.message?.includes('already exists')) {
                            errorMessage = 'El correo electrónico ya está registrado. Por favor, usa otro correo.';
                        } else if (error?.error?.message?.includes('password')) {
                            // Validación específica para el error de formato de contraseña
                            errorMessage = 'La contraseña no cumple con los requisitos de seguridad. Asegúrate de que incluya al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial.';
                        } else if (error?.error?.message) {
                            errorMessage = error.error.message;
                        }
    
                        Swal.fire({
                            title: 'Error al crear usuario',
                            text: errorMessage,
                            icon: 'error',
                            confirmButtonText: 'Aceptar'
                        });
    
                        reject(errorMessage);
                    }
                });
        });
    }

    onUsuarioSeleccionadoChange(id: number) {
        this.existingUserId = id; // Aquí se almacena el ID del usuario seleccionado
        console.log('Usuario seleccionado en el componente padre:', this.existingUserId);
    } 

    onEmailAvailableChange(available: boolean) {
        this.emailAvailable = available;
        if (available) {
            console.log('Correo disponible');
        } else {
            console.log('Correo no disponible');
        }
    }


    //#region Orquestamos inserciones
    submitAllFormsWithRollback() {
        this.isSaving = true;
        this.progress = 10;
        this.currentProcessText = 'Iniciando proceso de guardado...';
    
        let rollbackActions = [];  // Array para almacenar las acciones de rollback
        let stepCount = this.opcionSeleccionada === 'nuevo' ? 4 : 3;  // Número total de pasos dependiendo de la opción seleccionada
        let completedSteps = 0;    // Contador de pasos completados
    
        this.updateProcessText('Guardando datos de la empresa...');
    
        // 1. Insertar empresa
        this.submitAccountForm()
            .then(accountResponse => {
                this.idEmpresa = accountResponse.id;
                rollbackActions.push(() => this.rollbackAccountForm(accountResponse.id));  // Agregar rollback
                this.updateProgress(++completedSteps, stepCount);
                this.updateProcessText('Guardando sellos digitales...');
    
                // 2. Insertar sellos digitales
                return this.submitStampForm();
            })
            .then(stampResponse => {
                rollbackActions.push(() => this.rollbackStampForm(stampResponse.id));  // Agregar rollback
                this.updateProgress(++completedSteps, stepCount);
                this.updateProcessText('Guardando lugar de expedición...');
    
                // 3. Insertar lugar de expedición
                return this.submitLugarExpedicionForm();
            })
            .then(expedicionResponse => {
                rollbackActions.push(() => this.rollbackLugarExpedicionForm(expedicionResponse.id));  // Agregar rollback
                this.updateProgress(++completedSteps, stepCount);
    
                if (this.opcionSeleccionada === 'nuevo') {
                    this.updateProcessText('Guardando datos del usuario...');
    
                    // 4. Insertar nuevo usuario si la opción seleccionada es 'nuevo'
                    return this.submitNewUserForm();
                } else if (this.opcionSeleccionada === 'existente' && this.existingUserId) {
                    this.updateProcessText('Actualizando usuarios en la empresa...');
    
                    // Actualizar la empresa con el usuario existente seleccionado
                    return this.updateUserPermissionsUsers(this.idEmpresa, this.existingUserId);
                }
            })
            .then(() => {
                if (this.opcionSeleccionada === 'nuevo' || this.opcionSeleccionada === 'existente') {
                    this.updateProgress(++completedSteps, stepCount);
                }
                this.updateProcessText('Proceso completado.');
    
                Swal.fire({
                    title: 'Registro completado',
                    text: 'Todos los datos se han registrado correctamente.',
                    icon: 'success',
                    confirmButtonText: 'Aceptar'
                }).then(() => {
                    // Redirigir a la ruta especificada
                    this.router.navigate(['/factura/dashboard']);
                });
    
                this.isSaving = false;
            })
            .catch(error => {
                console.error('Error durante el proceso de registro:', error);
                this.isSaving = false;
                // Mostrar mensaje de error específico
                const errorMessage = this.getErrorMessage(error);
                Swal.fire({
                    title: 'Error en el registro',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
    
                // Retroceder al paso correspondiente según el error
                this.handleStepNavigationOnError(errorMessage);
    
                // Realizar rollback de todas las acciones realizadas
                this.executeRollbacks(rollbackActions);
            });
    }
    
    
    // handleStepNavigationOnError(errorMessage: string) {
    //     if (errorMessage.includes('Los datos de facturacion no son correctos')) {
    //         this.stepper.selectedIndex = 0;  // Retroceder al paso 1 (Datos de facturación)
    //     } else if (errorMessage.includes('Error al procesar los sellos digitales')) {
    //         this.stampForm.reset();
    //         this.fileCer = null;
    //         this.fileKey = null;
    //         this.stepper.selectedIndex = 1;  // Retroceder al paso 2 (Sellos digitales)
    //     } else if (errorMessage.includes('Error al registrar la dirección de expedición')) {
    //         this.stepper.selectedIndex = 2;  // Retroceder al paso 3 (Lugar de expedición)
    //     }
    // }

    handleStepNavigationOnError(errorMessage: string) {
        if (errorMessage.includes('Datos de facturación inválidos')) {
            this.stepper.selectedIndex = 0;  // Retroceder al paso 1 (Datos de facturación)
        } else if (errorMessage.includes('Error al procesar los archivos de los sellos')) {
            this.stepper.selectedIndex = 1;  // Retroceder al paso 2 (Sellos digitales)
        } else if (errorMessage.includes('Error al registrar la dirección de expedición')) {
            this.stepper.selectedIndex = 2;  // Retroceder al paso 3 (Lugar de expedición)
        } else if (errorMessage.includes('Datos del nuevo usuario inválidos') || errorMessage.includes('Ocurrió un error al guardar el usuario')) {
            this.stepper.selectedIndex = 3;  // Retroceder al paso 4 (Nuevo Usuario)
        }
    }
    
    
    getErrorMessage(error: any): string {
        let message = 'Ocurrió un error durante el proceso de registro. Se revertirán los cambios realizados.';
        
        if (error?.error?.message) {
            message = error.error.message;
        } else if (typeof error === 'string') {
            message = error;
        }
    
        return message;
    }
    

    // Ejecuta todas las acciones de rollback
    executeRollbacks(rollbackActions) {
        rollbackActions.reverse();  // Revertir el orden de las acciones
        const rollbackPromises = rollbackActions.map(action => action());

        Promise.all(rollbackPromises)
            .then(() => {
                console.log('Rollback completado exitosamente.');
            })
            .catch(rollbackError => {
                console.error('Error durante el rollback:', rollbackError);
            });
    }


    updateProgress(completedSteps: number, stepCount: number) {
        this.progress = (completedSteps / stepCount) * 100;
    }

    updateProcessText(text: string) {
        this.currentProcessText = text;
    }

    submitAccountForm(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.accountForm.markAllAsTouched();
            this.accountForm.updateValueAndValidity();

            if (!this.accountForm.valid) {
                return reject('Datos de facturación inválidos');
            }

            const data = {
                "data": {
                    "rfc": this.accountForm.value.rfc,
                    "cat_tipo_persona": this.accountForm.value.idTipoPersona,
                    "cat_giro": this.accountForm.value.idGiro,
                    "razonSocial": this.accountForm.value.razonSocial,
                    "nombreComercial": this.accountForm.value.nombreComercial,
                    "cat_regimen_fiscal": this.accountForm.value.idRegimenFiscal,
                    "cat_tamanio_empresa": this.accountForm.value.idTamanioEmpresa,
                    "email": 'noaplica@email.com',
                    "telefono": '',
                    "users_permissions_users": {
                        "connect": [this.user?.id]
                    }
                }
            };

            this.coalService.postService(`ope-empresas`, data).subscribe({
                next: (emp: any) => {
                    this.actualizaEmpresas(emp.data.id).then(res => {
                        resolve(emp.data); // Resolver la promesa con los datos de la empresa
                        this.Observable$.next(1);
                    }).catch(error => {
                        reject('Error al registrar la empresa');
                        console.log(error);
                    });
                },
                error: (error: any) => {
                    reject('Error al registrar la empresa');
                }
            });
        });
    }

    rollbackAccountForm(id: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.coalService.deleteService(`ope-empresas/${id}`).subscribe(
                () => resolve(),
                error => reject('Error al realizar rollback de los datos de la empresa')
            );
        });
    }

    submitStampForm(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.stampForm.markAllAsTouched();

            const params = {
                data: {
                    cer: this.fileCer.id,
                    key: this.fileKey.id,
                    idEmpresa: this.idEmpresa,
                    password: this.stampForm.value.contrasenia
                }
            };

            console.log('submitStampForm', params);


            this.coalService.postService('ope-empresa-emisores', params)
                .subscribe((res: any) => {
                    // this.Observable$.next(1);
                    resolve(res.data); // Resolver la promesa con los datos del emisor
                }, (error: any) => {
                    const message = (error?.error?.error?.message ?? false)
                        ? 'No pudimos procesar tu solicitud por la siguiente razón: ' + error.error.error.message + '. Por favor, verifica tu información y vuelve a intentarlo.'
                        : "Ocurrió un error al procesar los archivos";
                    reject('Error al procesar los archivos de los sellos');
                });
        });
    }

    rollbackStampForm(id: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.coalService.deleteService(`ope-empresa-emisores/${id}`).subscribe(
                () => resolve(),
                error => reject('Error al realizar rollback de los sellos')
            );
        });
    }

    submitLugarExpedicionForm(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.lugarExpedicionForm.markAllAsTouched();
            this.lugarExpedicionForm.updateValueAndValidity();

            if (!this.lugarExpedicionForm.valid) {
                return reject('Datos de lugar de expedición inválidos');
            }

            const data = {
                calle: this.lugarExpedicionForm.value.calle,
                noExterior: this.lugarExpedicionForm.value.noExterior,
                noInterior: this.lugarExpedicionForm.value.noInterior,
                pais: this.lugarExpedicionForm.value.pais,
                estado: this.lugarExpedicionForm.value.estado,
                municipio: this.lugarExpedicionForm.value.municipio,
                asentamiento: undefined,
                nombre: this.lugarExpedicionForm.value.nombreLugar,
                descripcion: this.lugarExpedicionForm.value.descripcionLugar,
                cp: this.lugarExpedicionForm.value.CP,
                isColoniaManual: false,
                coloniaManual: undefined,
                ope_empresa: this.idEmpresa
            };

            this.coalService.postService('ope-direccion-empresas/newAddress', data).subscribe({
                next: (res: any) => {
                    resolve(res.data); // Resolver la promesa con los datos de la dirección
                },
                error: (err: any) => {
                    reject('Error al registrar la dirección de expedición');
                }
            });
        });
    }

    rollbackLugarExpedicionForm(id: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.coalService.deleteService(`ope-direccion-empresas/${id}`).subscribe(
                () => resolve(),
                error => reject('Error al realizar rollback de la dirección de expedición')
            );
        });
    }
    //#endregion

    //#region controla empresa
    operacion: any;

    getEmpresas() {
        return new Promise((resolve, reject) => {
            const query = `ope-empresas?filters[users_permissions_users][id]=${this.user.id}&populate=logo,ope_empresa_estatus_hs.ope_empresa_estatus&sort=createdAt:ASC`;
            this.coalService.getService(query)
                .subscribe({ next: (res: any) => resolve(res), error: (error: any) => reject(error) });
        });
    }

    actualizaEmpresas(idEmpresa: number) {
        return new Promise((resolve, reject) => {
            this.getEmpresas().then((res: any) => {
                const myClonedState = { ...this.state, company: Object.assign([], res.data) };
                this.store.dispatch(UpdateData({ seguridad: myClonedState }));

                const empresa = res.data.find(e => e.id == idEmpresa);
                this.store.dispatch(SeleccionarEmpresa({ empresaSeleccionada: empresa }));

                resolve({ success: true });
            }).catch((error: any) => reject(error));
        });
    }

    getOperacion() {
        return new Promise((resolve, reject) => {
            this.coalService.getService(`ope-empresas?filters[id]=${this.idEmpresa}&populate[0]=cat_tipo_persona,cat_giro,cat_tamanio_empresa,cat_regimen_fiscal,logo`).subscribe({
                next: (res: any) => {
                    this.operacion = res?.data[0];
                    this.infoUserModule();
                    resolve({ success: true, data: res?.data });
                }, error: (error: any) => {
                    console.log(error);
                    resolve({ success: false, error: error });
                }
            }
            );
        });
    }

    infoUserModule() {
        this.store.select(selectAuthState).pipe(
            take(1), // Esto asegura que sólo tomes la primera emisión y luego te desuscribas
            switchMap((state: any) => {
                const userId = state.seguridad.user.id;
                return this.coalService.getService(`ope-empresas?filters[users_permissions_users][id]=${userId}&populate=logo,ope_empresa_estatus_hs.ope_empresa_estatus&sort=createdAt:ASC`);
            }),
            withLatestFrom(this.store.select(selectUtilsState)) // Con esto combinas el observable anterior con el estado actual de utils
        ).subscribe(([res, utilsState]: [any, any]) => {
            const empresaSeleccionada = utilsState?.empresaSeleccionada?.id;
            const filteredEmpresa = res.data.find(empresa => empresa.id === empresaSeleccionada);
            this.store.dispatch(SeleccionarEmpresa({ empresaSeleccionada: filteredEmpresa }));
            localStorage.setItem("empresaSeleccionada", JSON.stringify(filteredEmpresa));
        }, (error: any) => {
        });
    }
    //#endregion

    updateUserPermissionsUsers(idEmpresa: number, existingUserId: number): Promise<any> {
        return new Promise((resolve, reject) => {
            // Primero, obtenemos la empresa con los usuarios actuales
            this.coalService.getService(`ope-empresas/${idEmpresa}?populate=users_permissions_users`)
                .subscribe({
                    next: (res: any) => {
                        if (res && res.data) {
                            const empresa = res.data;
                            const usuariosActuales = empresa.attributes.users_permissions_users.data.map(user => user.id);
    
                            // Verificamos si el usuario ya está asociado
                            if (!usuariosActuales.includes(existingUserId)) {
                                usuariosActuales.push(existingUserId); // Agregamos el nuevo usuario
                            }
    
                            // Preparamos los datos para la actualización
                            const data = {
                                data: {
                                    users_permissions_users: {
                                        connect: usuariosActuales // Conectamos todos los IDs de usuarios
                                    }
                                }
                            };
    
                            // Hacemos la actualización
                            this.coalService.putService(`ope-empresas/${idEmpresa}`, data)
                                .subscribe({
                                    next: (updateRes: any) => {
                                        resolve(updateRes.data); // Resolver la promesa con los datos actualizados
                                    },
                                    error: (error: any) => {
                                        reject('Error al actualizar los usuarios en la empresa');
                                    }
                                });
                        } else {
                            reject('Empresa no encontrada');
                        }
                    },
                    error: (error: any) => {
                        reject('Error al obtener la empresa');
                    }
                });
        });
    }
    
}